import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SEO from '../../components/SEO';
import ChatShell from '../../components/chat/ChatShell';
import ChatTimelinePanel from '../../components/chat/ChatTimelinePanel';
import ChatAssetAttachment from '../../components/chat/ChatAssetAttachment';
import ChatLibraryPanel from '../../components/chat/ChatLibraryPanel';
import ChatSaveToLibraryButton from '../../components/chat/ChatSaveToLibraryButton';
import SupportCaseStatusChip from '../../components/chat/SupportCaseStatusChip';
import { chatApiClient } from '../../chatApiClient';
import { supportCasesApiClient } from '../../supportCasesApiClient';
import {
  ChatAsset,
  ChatLibraryEntry,
  ChatLibrarySourceType,
  ChatDetail,
  ChatReactionEmoji,
} from '../../models/Chat';
import { SupportCase, SupportCaseStatus } from '../../models/SupportCase';
import { uploadAssetFile } from '../../utils/uploadAssetFile';
import {
  formatFileSize,
  formatChatTimestamp,
  findChatLibraryEntry,
  isTimelineFeedKind,
  isChatImageAsset,
  isChatLibrarySaved,
  chatBasePath,
  chatLibrarySourceKey,
  chatMemberCountLabel,
  chatParticipants,
  chatPartnerLabel,
  chatTitleLabel,
  isOrganizationScopedChat,
  isOrganizationSupportChat,
  isSupportCaseChat,
  isAdminChatInbox,
} from '../../utils/chat';
import { supportCaseByChatId } from '../../utils/supportCases';
import { buildChatAvatarMap } from '../../utils/chatAvatars';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { chatPanelScrollSx } from '../../components/chat/chatLayout';
import { withViewerReaction, withoutViewerReaction } from '../../utils/chatReactions';
import { notifyChatUnreadCountChanged } from '../../utils/chatUnreadCount';
import { authService } from '../../services/AuthService';
import { getCachedProfileDisplay } from '../../services/profileDisplayCache';

const ChatDetailPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = chatBasePath(location.pathname);
  const adminInbox = isAdminChatInbox(location.pathname);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const markedReadChatIdRef = useRef<string | null>(null);
  const refreshingTimelineRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChatDetail | null>(null);
  const [supportCase, setSupportCase] = useState<SupportCase | null>(null);
  const [updatingCaseStatus, setUpdatingCaseStatus] = useState(false);
  const [caseStatusError, setCaseStatusError] = useState<string | null>(null);
  const currentUserId = authService.getUserId() ?? undefined;
  const isAdminUser = authService.getRoles().includes('ADMIN');
  const currentProfileImageUrl = getCachedProfileDisplay()?.profileImageUrl;
  const [tab, setTab] = useState(0);

  const [timelineBody, setTimelineBody] = useState('');
  const [postingTimeline, setPostingTimeline] = useState(false);
  const [refreshingTimeline, setRefreshingTimeline] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const [uploadingFile, setUploadingFile] = useState(false);

  const [libraryEntries, setLibraryEntries] = useState<ChatLibraryEntry[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [librarySavedKeys, setLibrarySavedKeys] = useState<Set<string>>(new Set());
  const [librarySavingKey, setLibrarySavingKey] = useState<string | null>(null);
  const [libraryRemovingEntryId, setLibraryRemovingEntryId] = useState<string | null>(null);
  const [reactionBusyItemId, setReactionBusyItemId] = useState<string | null>(null);

  const loadLibrary = useCallback(async () => {
    if (!chatId) return;
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const entries = await chatApiClient.listLibrary(chatId);
      setLibraryEntries(entries);
      setLibrarySavedKeys(
        new Set(entries.map((entry) => chatLibrarySourceKey(entry.sourceType, entry.sourceId)))
      );
    } catch {
      setLibraryError(t('chat.library.loadError', 'Failed to load library'));
    } finally {
      setLibraryLoading(false);
    }
  }, [chatId, t]);

  const loadChat = useCallback(async () => {
    if (!chatId) return;
    setLoading(true);
    setError(null);
    try {
      const chatDetail = await chatApiClient.getChat(chatId);
      setData(chatDetail);
      if (isSupportCaseChat(chatDetail.summary)) {
        try {
          const cases = await supportCasesApiClient.listSupportCases();
          setSupportCase(supportCaseByChatId(cases, chatId) ?? null);
        } catch {
          setSupportCase(null);
        }
      } else {
        setSupportCase(null);
      }
      void loadLibrary();
    } catch {
      setError(t('chat.detail.loadError', 'Failed to load chat'));
    } finally {
      setLoading(false);
    }
  }, [chatId, t, loadLibrary]);

  const refreshTimeline = useCallback(async () => {
    if (!chatId || refreshingTimelineRef.current) return;
    refreshingTimelineRef.current = true;
    setRefreshingTimeline(true);
    setTimelineError(null);
    try {
      const chatDetail = await chatApiClient.getChat(chatId);
      setData(chatDetail);
    } catch {
      setTimelineError(t('chat.timeline.refreshError', 'Failed to refresh messages'));
    } finally {
      refreshingTimelineRef.current = false;
      setRefreshingTimeline(false);
    }
  }, [chatId, t]);

  const handleUpdateCaseStatus = useCallback(
    async (status: SupportCaseStatus) => {
      if (!supportCase) return;
      setUpdatingCaseStatus(true);
      setCaseStatusError(null);
      try {
        const updated = await supportCasesApiClient.updateSupportCaseStatus(supportCase.id, status);
        setSupportCase(updated);
      } catch {
        setCaseStatusError(t('supportCases.status.updateError', 'Failed to update case status.'));
      } finally {
        setUpdatingCaseStatus(false);
      }
    },
    [supportCase, t]
  );

  useEffect(() => {
    void loadChat();
  }, [loadChat]);

  useEffect(() => {
    markedReadChatIdRef.current = null;
  }, [chatId]);

  const handleTimelineRendered = useCallback(() => {
    if (!chatId || tab !== 0 || markedReadChatIdRef.current === chatId) return;

    markedReadChatIdRef.current = chatId;
    void chatApiClient.markChatRead(chatId).then(() => {
      setData((current) =>
        current && current.summary.unreadCount > 0
          ? { ...current, summary: { ...current.summary, unreadCount: 0 } }
          : current
      );
      notifyChatUnreadCountChanged();
    });
  }, [chatId, tab]);

  const timelineItems = useMemo(() => {
    if (!data) return [];
    return [...data.items]
      .filter((item) => isTimelineFeedKind(item.kind))
      .sort(
        (a, b) =>
          new Date(a.createdAt.endsWith('Z') ? a.createdAt : `${a.createdAt}Z`).getTime() -
          new Date(b.createdAt.endsWith('Z') ? b.createdAt : `${b.createdAt}Z`).getTime()
      );
  }, [data]);

  const assetsById = useMemo(() => {
    const map = new Map<string, ChatAsset>();
    data?.assets.forEach((asset) => map.set(asset.id, asset));
    return map;
  }, [data]);

  const avatarByUserId = useMemo(
    () => (data ? buildChatAvatarMap([data.summary], currentUserId, currentProfileImageUrl) : new Map()),
    [data, currentUserId, currentProfileImageUrl]
  );

  const sortedAssets = useMemo(() => {
    if (!data) return [];
    return [...data.assets].sort(
      (a, b) =>
        new Date(b.createdAt.endsWith('Z') ? b.createdAt : `${b.createdAt}Z`).getTime() -
        new Date(a.createdAt.endsWith('Z') ? a.createdAt : `${a.createdAt}Z`).getTime()
    );
  }, [data]);

  const handlePostTimeline = async () => {
    if (!chatId) return;
    const body = timelineBody.trim();
    if (!body) return;
    setPostingTimeline(true);
    setTimelineError(null);
    try {
      await chatApiClient.addTimelineItem(chatId, { kind: 'MESSAGE', body });
      setTimelineBody('');
      await loadChat();
      setTab(0);
    } catch {
      setTimelineError(t('chat.timeline.postError', 'Failed to post to the timeline'));
    } finally {
      setPostingTimeline(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatId) return;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingFile(true);
    try {
      const url = await uploadAssetFile(file);
      await chatApiClient.addAsset(chatId, {
        kind: 'FILE',
        title: file.name,
        url,
        mimeType: file.type || undefined,
        sizeBytes: file.size,
      });
      await loadChat();
      setTab(0);
    } catch {
      setTimelineError(t('chat.files.uploadError', 'Failed to upload file'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSaveToLibrary = async (sourceType: ChatLibrarySourceType, sourceId: string) => {
    if (!chatId) return;
    const key = chatLibrarySourceKey(sourceType, sourceId);
    const existingEntry = findChatLibraryEntry(libraryEntries, sourceType, sourceId);

    setLibrarySavingKey(key);
    setLibraryError(null);
    try {
      if (existingEntry) {
        await chatApiClient.removeFromLibrary(chatId, existingEntry.id);
        setLibraryEntries((prev) => prev.filter((item) => item.id !== existingEntry.id));
        setLibrarySavedKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      } else {
        const entry = await chatApiClient.saveToLibrary(chatId, { sourceType, sourceId });
        setLibraryEntries((prev) => {
          const withoutDuplicate = prev.filter((item) => item.id !== entry.id);
          return [entry, ...withoutDuplicate];
        });
        setLibrarySavedKeys((prev) => new Set(prev).add(key));
      }
    } catch {
      setLibraryError(
        existingEntry
          ? t('chat.library.removeError', 'Failed to remove from library')
          : t('chat.library.saveError', 'Failed to save to library')
      );
    } finally {
      setLibrarySavingKey(null);
    }
  };

  const handleRemoveFromLibrary = async (entry: ChatLibraryEntry) => {
    if (!chatId) return;

    setLibraryRemovingEntryId(entry.id);
    setLibraryError(null);
    try {
      await chatApiClient.removeFromLibrary(chatId, entry.id);
      setLibraryEntries((prev) => prev.filter((item) => item.id !== entry.id));
      setLibrarySavedKeys((prev) => {
        const next = new Set(prev);
        next.delete(chatLibrarySourceKey(entry.sourceType, entry.sourceId));
        return next;
      });
    } catch {
      setLibraryError(t('chat.library.removeError', 'Failed to remove from library'));
    } finally {
      setLibraryRemovingEntryId(null);
    }
  };

  const patchItemReaction = (
    itemId: string,
    next: { reactions: ChatDetail['items'][number]['reactions']; reactionEmoji: ChatReactionEmoji | null }
  ) => {
    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) =>
          item.id === itemId ? { ...item, ...next } : item
        ),
      };
    });
  };

  const handleSetReaction = async (itemId: string, emoji: ChatReactionEmoji) => {
    if (!chatId || !currentUserId) return;
    const item = data?.items.find((entry) => entry.id === itemId);
    if (!item) return;
    const previous = {
      reactions: item.reactions ?? [],
      reactionEmoji: item.reactionEmoji ?? null,
    };
    patchItemReaction(itemId, withViewerReaction(previous, currentUserId, emoji));
    setReactionBusyItemId(itemId);
    setTimelineError(null);
    try {
      await chatApiClient.setReaction(chatId, itemId, emoji);
    } catch {
      patchItemReaction(itemId, previous);
      setTimelineError(t('chat.timeline.reactionError', 'Failed to update reaction'));
    } finally {
      setReactionBusyItemId(null);
    }
  };

  const handleClearReaction = async (itemId: string) => {
    if (!chatId || !currentUserId) return;
    const item = data?.items.find((entry) => entry.id === itemId);
    if (!item) return;
    const previous = {
      reactions: item.reactions ?? [],
      reactionEmoji: item.reactionEmoji ?? null,
    };
    patchItemReaction(itemId, withoutViewerReaction(previous, currentUserId));
    setReactionBusyItemId(itemId);
    setTimelineError(null);
    try {
      await chatApiClient.clearReaction(chatId, itemId);
    } catch {
      patchItemReaction(itemId, previous);
      setTimelineError(t('chat.timeline.reactionError', 'Failed to update reaction'));
    } finally {
      setReactionBusyItemId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, width: '100%' }}>
        <Alert severity="error">{error || t('chat.detail.loadError', 'Failed to load chat')}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(basePath)}>
          {t('common.back', 'Back')}
        </Button>
      </Box>
    );
  }

  const teamChat = isOrganizationScopedChat(data.summary);
  const supportCaseChat = isSupportCaseChat(data.summary);
  const title = chatTitleLabel(data.summary, currentUserId, t, {
    inbox: adminInbox ? 'admin' : 'member',
    supportCaseSubject: supportCase?.subject,
  });
  const memberCountLabel = chatMemberCountLabel(data.summary, t);
  const members = chatParticipants(data.summary);
  const partnerForCopy = teamChat || supportCaseChat
    ? title
    : chatPartnerLabel(data.summary, currentUserId, t);
  const isCaseRequester =
    currentUserId != null && supportCase?.requesterUserId === currentUserId;
  const canUpdateCaseStatus = Boolean(supportCase) && (isAdminUser || isCaseRequester);

  const supportCaseComposerActions =
    supportCase && canUpdateCaseStatus ? (
      <>
        {supportCase.status === 'OPEN' && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={updatingCaseStatus}
              onClick={() => void handleUpdateCaseStatus('CLOSED')}
            >
              {t('supportCases.status.close', 'Close')}
            </Button>
            {isAdminUser && (
              <Button
                size="small"
                variant="contained"
                disabled={updatingCaseStatus}
                onClick={() => void handleUpdateCaseStatus('RESOLVED')}
              >
                {t('supportCases.status.resolve', 'Resolve')}
              </Button>
            )}
          </>
        )}
        {supportCase.status === 'RESOLVED' && (
          <>
            {isAdminUser && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={updatingCaseStatus}
                onClick={() => void handleUpdateCaseStatus('CLOSED')}
              >
                {t('supportCases.status.close', 'Close')}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={updatingCaseStatus}
              onClick={() => void handleUpdateCaseStatus('OPEN')}
            >
              {t('supportCases.status.reopen', 'Reopen')}
            </Button>
          </>
        )}
        {supportCase.status === 'CLOSED' && (
          <Button
            size="small"
            variant="outlined"
            disabled={updatingCaseStatus}
            onClick={() => void handleUpdateCaseStatus('OPEN')}
          >
            {t('supportCases.status.reopen', 'Reopen')}
          </Button>
        )}
      </>
    ) : null;

  return (
    <>
      <SEO title={title} noindex />
      <input ref={fileInputRef} type="file" hidden onChange={(e) => void handleFileUpload(e)} />

      <ChatShell
        partnerName={title}
        subtitle={teamChat ? memberCountLabel : null}
        titleBadge={
          supportCaseChat && supportCase ? (
            <SupportCaseStatusChip status={supportCase.status} />
          ) : null
        }
        isTeamChat={teamChat}
        isSupportChat={isOrganizationSupportChat(data.summary) || supportCaseChat}
        members={members}
        avatarByUserId={avatarByUserId}
        currentUserId={currentUserId}
        tab={tab}
        onTabChange={setTab}
        onBack={() => navigate(basePath)}
        onRefresh={() => void refreshTimeline()}
        refreshingTimeline={refreshingTimeline}
        refreshDisabled={postingTimeline || uploadingFile}
      >
        {caseStatusError && (
          <Alert severity="error" sx={{ mx: 2, mt: 1.5 }} onClose={() => setCaseStatusError(null)}>
            {caseStatusError}
          </Alert>
        )}
        {tab === 0 && (
          <ChatTimelinePanel
            chatId={chatId!}
            items={timelineItems}
            assetsById={assetsById}
            avatarByUserId={avatarByUserId}
            librarySavedKeys={librarySavedKeys}
            librarySavingKey={librarySavingKey}
            partnerName={partnerForCopy}
            currentUserId={currentUserId}
            timelineBody={timelineBody}
            postingTimeline={postingTimeline || uploadingFile}
            refreshingTimeline={refreshingTimeline}
            timelineError={timelineError}
            locale={i18n.language}
            t={t}
            onBodyChange={setTimelineBody}
            onPost={() => void handlePostTimeline()}
            onAttachFile={() => fileInputRef.current?.click()}
            onSaveToLibrary={(sourceType, sourceId) => void handleSaveToLibrary(sourceType, sourceId)}
            onSetReaction={(itemId, emoji) => void handleSetReaction(itemId, emoji)}
            onClearReaction={(itemId) => void handleClearReaction(itemId)}
            reactionBusyItemId={reactionBusyItemId}
            onTimelineRendered={handleTimelineRendered}
            composerActions={supportCaseComposerActions}
          />
        )}

        {tab === 1 && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={chatPanelScrollSx}>
              <Stack spacing={2}>
                {sortedAssets.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {t('chat.files.empty', 'No files yet.')}
                  </Typography>
                ) : (
                  sortedAssets.map((asset) => (
                    <Stack
                      key={asset.id}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'rgba(255,255,255,0.02)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                      }}
                    >
                      {asset.kind === 'LINK' ? (
                        <LinkRoundedIcon sx={{ color: 'primary.light' }} />
                      ) : isChatImageAsset(asset) ? (
                        <ChatAssetAttachment asset={asset} variant="list" />
                      ) : (
                        <InsertDriveFileOutlinedIcon sx={{ color: 'primary.light' }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Link
                          href={asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="body2"
                          sx={{ fontWeight: 600, display: 'block' }}
                        >
                          {asset.title}
                        </Link>
                        <Typography variant="caption" color="text.secondary">
                          {asset.uploadedByName || t('chat.timeline.unknownAuthor', 'Someone')}
                          {' · '}
                          {formatChatTimestamp(asset.createdAt, i18n.language)}
                          {formatFileSize(asset.sizeBytes, i18n.language)
                            ? ` · ${formatFileSize(asset.sizeBytes, i18n.language)}`
                            : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          component="a"
                          href={asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t('chat.files.openInNewTab', 'Open in new tab')}
                        >
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                        <ChatSaveToLibraryButton
                          saved={isChatLibrarySaved(librarySavedKeys, 'ASSET', asset.id)}
                          saving={librarySavingKey === chatLibrarySourceKey('ASSET', asset.id)}
                          onClick={() => void handleSaveToLibrary('ASSET', asset.id)}
                        />
                      </Stack>
                    </Stack>
                  ))
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {tab === 2 && (
          <ChatLibraryPanel
            entries={libraryEntries}
            assetsById={assetsById}
            loading={libraryLoading}
            error={libraryError}
            removingEntryId={libraryRemovingEntryId}
            locale={i18n.language}
            onRemoveEntry={(entry) => void handleRemoveFromLibrary(entry)}
          />
        )}
      </ChatShell>
    </>
  );
};

export default ChatDetailPage;
